
import { useState } from "react";
import { Clock, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { TimeEntryForm, TimeEntry } from "@/components/TimeEntryForm";
import { Project } from "@/types/Project";

interface AddTimeEntryButtonProps {
  hasProjects: boolean;
  onCreateProject?: () => void;
  projects?: Project[];
  onAddTimeEntry?: (entry: TimeEntry) => void;
}

export function AddTimeEntryButton({ 
  hasProjects, 
  onCreateProject, 
  projects = [], 
  onAddTimeEntry 
}: AddTimeEntryButtonProps) {
  const [showNoProjectsDialog, setShowNoProjectsDialog] = useState(false);
  const [showTimeEntryDialog, setShowTimeEntryDialog] = useState(false);
  const { toast } = useToast();

  const handleAddTimeEntry = () => {
    if (hasProjects) {
      setShowTimeEntryDialog(true);
    } else {
      setShowNoProjectsDialog(true);
    }
  };

  const handleTimeEntrySubmit = (entry: TimeEntry) => {
    if (onAddTimeEntry) {
      onAddTimeEntry(entry);
      setShowTimeEntryDialog(false);
      toast({
        title: "Time entry added",
        description: `Added ${Math.floor(entry.minutes / 60)}h ${entry.minutes % 60}m`,
      });
    }
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              onClick={handleAddTimeEntry}
              variant="default"
              size="sm"
              className="gap-2"
            >
              <Clock className="h-4 w-4" />
              Add Time Entry
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Track time for your projects</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Dialog for no projects */}
      <Dialog open={showNoProjectsDialog} onOpenChange={setShowNoProjectsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create a project first</DialogTitle>
            <DialogDescription>
              You need to create a project before you can add time entries.
            </DialogDescription>
          </DialogHeader>
          
          <EmptyState
            title="No projects yet"
            description="Create your first project to start tracking time."
            actionLabel="Create New Project"
            onAction={() => {
              setShowNoProjectsDialog(false);
              if (onCreateProject) {
                onCreateProject();
              }
            }}
            icon={<PlusCircle className="h-12 w-12 opacity-20" />}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog for time entry form */}
      <Dialog open={showTimeEntryDialog} onOpenChange={setShowTimeEntryDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Time Entry</DialogTitle>
            <DialogDescription>
              Track your time for a project.
            </DialogDescription>
          </DialogHeader>
          
          <TimeEntryForm 
            onSubmit={handleTimeEntrySubmit} 
            projects={projects} 
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
